/**
 * @fileoverview Telegram Mini App 工具函数
 * @description 封装 Telegram WebApp SDK 的检测、初始化和数据获取
 * MVC 架构：工具层，不包含业务逻辑
 * @module lib/telegram
 */

// ============================================================
// 类型定义（与 Telegram WebApp SDK 对齐）
// ============================================================

export interface TelegramUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  language_code?: string;
  is_premium?: boolean;
  photo_url?: string;
}

export interface TelegramWebApp {
  initData: string;
  initDataUnsafe: {
    user?: TelegramUser;
    start_param?: string;
    auth_date?: number;
    hash?: string;
  };
  version: string;
  platform: string;
  colorScheme: 'light' | 'dark';
  themeParams: Record<string, string>;
  isExpanded: boolean;
  viewportHeight: number;
  viewportStableHeight: number;
  isClosingConfirmationEnabled: boolean;
  ready: () => void;
  expand: () => void;
  close: () => void;
  enableClosingConfirmation: () => void;
  disableClosingConfirmation: () => void;
  setHeaderColor: (color: string) => void;
  setBackgroundColor: (color: string) => void;
  MainButton: {
    text: string;
    color: string;
    textColor: string;
    isVisible: boolean;
    isActive: boolean;
    show: () => void;
    hide: () => void;
    enable: () => void;
    disable: () => void;
    setText: (text: string) => void;
    onClick: (fn: () => void) => void;
    offClick: (fn: () => void) => void;
  };
  BackButton: {
    isVisible: boolean;
    show: () => void;
    hide: () => void;
    onClick: (fn: () => void) => void;
    offClick: (fn: () => void) => void;
  };
  HapticFeedback: {
    impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void;
    notificationOccurred: (type: 'error' | 'success' | 'warning') => void;
    selectionChanged: () => void;
  };
}

declare global {
  interface Window {
    Telegram?: {
      WebApp?: TelegramWebApp;
    };
  }
}

// ============================================================
// 工具函数
// ============================================================

/**
 * 检测当前是否在 Telegram Mini App 环境中
 * @returns 如果在真实 TG 客户端中运行，返回 true
 *
 * 检测逻辑：
 * - window.Telegram.WebApp 对象存在
 * - platform 字段不为 'unknown'（普通浏览器加载 SDK 后 platform='unknown'）
 * - platform 字段不为空字符串
 */
export function isTelegramMiniApp(): boolean {
  const tg = window.Telegram?.WebApp;
  if (!tg) return false;
  // 在真实 TG 客户端中，platform 为 'android'/'ios'/'tdesktop'/'macos'/'web' 等
  // 在普通浏览器中加载 SDK 后，platform 为 'unknown'
  const platform = tg.platform;
  return typeof platform === 'string' && platform.length > 0 && platform !== 'unknown';
}

/**
 * 获取 Telegram WebApp 实例
 * @returns TelegramWebApp 实例，不在 TG 环境时返回 null
 */
export function getTelegramWebApp(): TelegramWebApp | null {
  return window.Telegram?.WebApp ?? null;
}

/**
 * 获取 Telegram initData（用于后端验证）
 * @returns initData 字符串，不在 TG 环境时返回 null
 */
export function getTelegramInitData(): string | null {
  const tg = window.Telegram?.WebApp;
  if (!tg || !tg.initData) return null;
  return tg.initData;
}

/**
 * 获取 Telegram 用户信息（从 initDataUnsafe，仅供 UI 展示）
 * @returns TelegramUser 对象，不在 TG 环境时返回 null
 */
export function getTelegramUser(): TelegramUser | null {
  const tg = window.Telegram?.WebApp;
  return tg?.initDataUnsafe?.user ?? null;
}

/**
 * 初始化 Telegram Mini App
 * 调用 ready()、expand() 并设置主题色
 */
export function initTelegramApp(): void {
  const tg = window.Telegram?.WebApp;
  if (!tg) return;
  // 通知 TG 客户端 Mini App 已准备好
  tg.ready();
  // 展开到全屏
  tg.expand();
  // 设置深色主题配色
  try {
    tg.setHeaderColor('#0B0E11');
    tg.setBackgroundColor('#0B0E11');
  } catch {
    // 部分旧版本 SDK 不支持，忽略错误
  }
}

/**
 * 获取 Telegram 启动参数（start_param，用于解析邀请码等）
 * @returns start_param 字符串，不存在时返回 null
 */
export function getTelegramStartParam(): string | null {
  const tg = window.Telegram?.WebApp;
  return tg?.initDataUnsafe?.start_param ?? null;
}

/**
 * 触发 Telegram 触觉反馈
 */
export function hapticFeedback(type: 'success' | 'error' | 'warning' = 'success'): void {
  try {
    window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred(type);
  } catch {
    // 忽略不支持的环境
  }
}
